import { ChangeTemporaryPasswordPage } from '@src/authentication/pages/ChangeTemporaryPasswordPage';
import { ConfirmResetPasswordPage } from '@src/authentication/pages/ConfirmResetPasswordPage';
import { ForgotPasswordPage } from '@src/authentication/pages/ForgotPasswordPage';
import { LoginPage } from '@src/authentication/pages/LoginPage';
import { useAuthentication } from '@src/authentication/useAuthentication';
import { CreateChangeRequestPage } from '@src/changeRequest/CreateChangeRequestPage';
import { AdminAddClient } from '@src/clients/AdminAddClient';
import { AdminManageClients } from '@src/clients/AdminManageClients';
import { ClientPage } from '@src/clients/ClientPage';
import { ClientUserApp } from '@src/clientUser/ClientUserApp';
import { isClientUser } from '@src/clientUser/useClientUser';
import { Layout } from '@src/common/components/Layout';
import { LoadingPage } from '@src/common/components/LoadingPage';
import { ToastProvider } from '@src/common/context/ToastProvider';
import { PermitMeasurementContextProvider } from '@src/common/measurements/PermitMeasurementContextProvider';
import { UsersSideNav } from '@src/common/UsersSideNav';
import { DocumentsClient } from '@src/documents/services/DocumentsClient';
import { AddUser } from '@src/manageUsers/AddUser';
import { EditUser } from '@src/manageUsers/EditUser';
import { ManageUsers } from '@src/manageUsers/ManageUsers';
import { UsersClient } from '@src/manageUsers/services/UsersClient';
import { AddOrderPermits } from '@src/orders/AddOrderPermits';
import { AdminEditOrder } from '@src/orders/AdminEditOrder';
import { CompletePermitFormProvider } from '@src/orders/CompletePermitFormProvider';
import { CreateOrder } from '@src/orders/CreateOrder';
import { DuplicateOrderLoader } from '@src/orders/DuplicateOrderLoader';
import { EditOrderProvider } from '@src/orders/EditOrderProvider';
import { OrdersDashboard } from '@src/orders/OrdersDashboard';
import { OrderValuesContextProvider } from '@src/orders/OrderValuesContextProvider';
import { OrdersClient } from '@src/orders/services/OrdersClient';
import { ViewOrderProvider } from '@src/orders/ViewOrderProvider';
import { CompletePermit } from '@src/permits/completePermit/CompletePermit';
import { PermitSearch } from '@src/permits/permitSearch/PermitSearch';
import { ModifyProcessWithJurisdictionPermit } from '@src/permits/processWithJurisdiction/ModifyProcessWithJurisdictionPermit';
import { ProcessWithJurisdictionPermit } from '@src/permits/processWithJurisdiction/ProcessWithJurisdictionPermit';
import { PermitApplicationClient } from '@src/permits/services/PermitApplicationClient';
import { AddPermitType } from '@src/permitTypesManager/AddPermitType';
import { EditPermitType } from '@src/permitTypesManager/EditPermitType';
import type { ExistingPermitTypeDefinition } from '@src/permitTypesManager/PermitTypeDefinition';
import { PermitTypeClient } from '@src/permitTypesManager/services/PermitTypeClient';
import { PublicPortal } from '@src/publicPortal/PublicPortal';
import { Reports } from '@src/reports/Reports';
import { AwaitRouterData } from '@src/routing/AwaitRouterData';
import { ClientsNavigation } from '@src/routing/ClientsNavigation';
import { clientTractorsAndTrailersRoutes } from '@src/routing/ClientTractorsAndTrailersRoutes';
import { GlobalErrorBoundary } from '@src/routing/components/GlobalErrorBoundary';
import { NoAuth } from '@src/routing/components/NoAuth';
import { RequireAuth } from '@src/routing/components/RequireAuth';
import { UnknownRoute } from '@src/routing/components/UnknownRoute';
import { Loaders } from '@src/routing/Loaders';
import { Navigation } from '@src/routing/Navigation';
import { Refreshable } from '@src/routing/Refreshable';
import { SettingsPage } from '@src/settings/SettingsPage';
import { createBrowserRouter, defer, RouterProvider } from 'react-router-dom';

import { ReplacePermitApplicationType } from '../orders/ReplacePermitApplicationType';

const router = createBrowserRouter([
  {
    path: Navigation.login,
    element: (
      <ToastProvider>
        <NoAuth />
      </ToastProvider>
    ),
    children: [{ path: '', element: <LoginPage /> }],
  },
  {
    path: Navigation.confirmResetPassword,
    element: (
      <ToastProvider>
        <ConfirmResetPasswordPage />
      </ToastProvider>
    ),
    errorElement: <GlobalErrorBoundary />,
  },
  {
    path: Navigation.changeTemporaryPassword,
    element: (
      <ToastProvider>
        <ChangeTemporaryPasswordPage />
      </ToastProvider>
    ),
    errorElement: <GlobalErrorBoundary />,
  },
  {
    path: Navigation.forgotPassword,
    element: (
      <ToastProvider>
        <NoAuth />
      </ToastProvider>
    ),
    children: [{ path: '', element: <ForgotPasswordPage /> }],
  },
  {
    path: Navigation.home,
    element: (
      <RequireAuth>
        <Layout SideNav={UsersSideNav} />
      </RequireAuth>
    ),
    children: [
      {
        path: '',
        element: <OrdersDashboard />,
      },
      {
        path: Navigation.users.PATH,
        children: [
          {
            path: '',
            element: <ManageUsers />,
          },
          { path: Navigation.users.add.RELATIVE, element: <AddUser /> },
          {
            path: Navigation.users.edit.RELATIVE,
            loader: async ({ params }) => {
              if (!params.userId) return;
              return defer({
                user: UsersClient.find(params.userId),
              });
            },
            element: (
              <AwaitRouterData path="user">
                {(data) => <EditUser user={data} />}
              </AwaitRouterData>
            ),
          },
        ],
      },
      {
        path: Navigation.orders.PATH,
        children: [
          {
            path: Navigation.orders.create.RELATIVE,
            element: <CreateOrder />,
          },
          {
            path: Navigation.orders.order.PATH,
            loader: async ({ params }) => {
              if (!params.orderId) return;
              return { orderId: params.orderId };
            },
            element: (
              <AwaitRouterData path="orderId">
                {(orderId) => <OrderValuesContextProvider orderId={orderId} />}
              </AwaitRouterData>
            ),
            children: [
              {
                path: Navigation.orders.edit.PATH,
                loader: async ({ params }) => {
                  if (!params.orderId) return;
                  return { orderId: params.orderId };
                },
                element: (
                  <AwaitRouterData path="orderId">
                    {(orderId) => (
                      <EditOrderProvider
                        orderId={orderId}
                        EditOrderPage={AdminEditOrder}
                      />
                    )}
                  </AwaitRouterData>
                ),
              },
              {
                path: Navigation.orders.completePermitForm.PATH,
                loader: async ({ params }) => {
                  if (!params.orderId || !params.permitApplicationId) return;
                  return {
                    params: {
                      orderId: params.orderId,
                      permitApplicationId: params.permitApplicationId,
                    },
                  };
                },
                element: (
                  <AwaitRouterData path="params">
                    {({ orderId, permitApplicationId }) => (
                      <CompletePermitFormProvider
                        orderId={orderId}
                        permitId={permitApplicationId}
                        EditOrderPage={AdminEditOrder}
                      />
                    )}
                  </AwaitRouterData>
                ),
              },
              {
                path: Navigation.orders.view.PATH,
                loader: async ({ params }) => {
                  if (!params.orderId) return;
                  return { orderId: params.orderId };
                },
                element: (
                  <AwaitRouterData path="orderId">
                    {(orderId) => <ViewOrderProvider orderId={orderId} />}
                  </AwaitRouterData>
                ),
              },
              {
                path: Navigation.orders.changeRequest.PATH,
                loader: async ({ params }) => {
                  if (!params.orderId) return;
                  return { orderId: params.orderId };
                },
                element: (
                  <AwaitRouterData path="orderId">
                    {(orderId) => (
                      <EditOrderProvider
                        orderId={orderId}
                        EditOrderPage={CreateChangeRequestPage}
                      />
                    )}
                  </AwaitRouterData>
                ),
              },
              {
                path: Navigation.orders.duplicate.PATH,
                loader: async ({ params }) => {
                  if (!params.orderId) return;
                  return { orderId: params.orderId };
                },
                element: (
                  <AwaitRouterData path="orderId">
                    {(orderId) => <DuplicateOrderLoader orderId={orderId} />}
                  </AwaitRouterData>
                ),
              },
              {
                path: Navigation.orders.addPermits.PATH,
                loader: async ({ params }) => {
                  if (!params.orderId) return;
                  const order = await OrdersClient.find(params.orderId);
                  return { order };
                },
                element: (
                  <AwaitRouterData path="order">
                    {(order) => <AddOrderPermits order={order} />}
                  </AwaitRouterData>
                ),
              },
              {
                path: Navigation.orders.replacePermitType.PATH,
                loader: async ({ params }) => {
                  if (!params.orderId || !params.originPermitId) return;
                  return {
                    params: {
                      orderId: params.orderId,
                      originPermitId: params.originPermitId,
                    },
                  };
                },
                element: (
                  <AwaitRouterData path="params">
                    {({ orderId, originPermitId }) => (
                      <ReplacePermitApplicationType
                        orderId={orderId}
                        originPermitId={originPermitId}
                      />
                    )}
                  </AwaitRouterData>
                ),
              },
            ],
          },
          {
            path: Navigation.orders.processPermit.RELATIVE,
            loader: async ({ params }) => {
              if (!params.permitId || !params.orderId) return;
              const order = OrdersClient.find(params.orderId);
              const permitApplication =
                PermitApplicationClient.findPermitApplication(params.permitId);
              return defer({
                values: Promise.all([permitApplication, order]),
              });
            },
            element: (
              <AwaitRouterData path="values">
                {([permitApplication, order]) => (
                  <Refreshable
                    children={() => (
                      <PermitMeasurementContextProvider
                        permitTypeCurrency={
                          permitApplication.permitTypeCurrency
                        }
                      >
                        <ProcessWithJurisdictionPermit
                          orderPermitApplication={permitApplication}
                          order={order}
                        />
                      </PermitMeasurementContextProvider>
                    )}
                  />
                )}
              </AwaitRouterData>
            ),
          },
          {
            path: Navigation.orders.modifyStatePermit.RELATIVE,
            loader: async ({ params }) => {
              if (!params.permitId || !params.orderId) return;
              const order = await OrdersClient.find(params.orderId);
              const permitApplication =
                await OrdersClient.getPermitApplicationWithChangesPreview({
                  orderId: order.id,
                  permitId: params.permitId,
                });
              return defer({
                values: Promise.all([permitApplication, order]),
              });
            },
            element: (
              <AwaitRouterData path="values">
                {([permitApplication, order]) => (
                  <Refreshable
                    children={() => (
                      <PermitMeasurementContextProvider
                        permitTypeCurrency={
                          permitApplication.permitTypeCurrency
                        }
                      >
                        <ModifyProcessWithJurisdictionPermit
                          orderPermitApplication={permitApplication}
                          order={order}
                        />
                      </PermitMeasurementContextProvider>
                    )}
                  />
                )}
              </AwaitRouterData>
            ),
          },
          {
            path: Navigation.orders.completePermit.RELATIVE,
            loader: async ({ params }) => {
              if (!params.permitId || !params.orderId) return;

              const order = OrdersClient.find(params.orderId);
              const permit = PermitApplicationClient.findPermitApplication(
                params.permitId,
              );
              return defer({
                values: Promise.all([permit, order]),
              });
            },
            element: (
              <AwaitRouterData path="values">
                {([permit, order]) => (
                  <PermitMeasurementContextProvider
                    permitTypeCurrency={permit.permitTypeCurrency}
                  >
                    <CompletePermit permitApplication={permit} order={order} />
                  </PermitMeasurementContextProvider>
                )}
              </AwaitRouterData>
            ),
          },
        ],
      },
      {
        path: Navigation.clients.PATH,
        children: [
          {
            path: '',
            element: (
              <AdminManageClients
                addClientPageLink={ClientsNavigation.add.RELATIVE}
              />
            ),
          },
          {
            path: Navigation.clients.add.RELATIVE,
            element: <AdminAddClient />,
          },
          {
            path: Navigation.clients.get.RELATIVE,
            loader: Loaders.client.loader,
            element: (
              <AwaitRouterData path={Loaders.client.path}>
                {(data) => (
                  <Refreshable
                    children={({ refresh }) => (
                      <ClientPage client={data} refreshData={refresh} />
                    )}
                  />
                )}
              </AwaitRouterData>
            ),
          },
          ...clientTractorsAndTrailersRoutes,
        ],
      },
      {
        path: Navigation.permitSearch,
        element: <PermitSearch />,
      },
      {
        path: Navigation.reports,
        element: <Reports />,
      },
      {
        path: Navigation.settings.PATH,
        element: <SettingsPage />,
      },
      {
        path: Navigation.permitTypesManager.addType.PATH,
        loader: async ({ request }) => {
          const url = new URL(request.url);
          const duplicateFromId = url.searchParams.get(
            Navigation.permitTypesManager.addType.duplicateFromQueryParam,
          );
          if (!duplicateFromId) {
            return defer({ duplicateSource: undefined });
          }
          const duplicateSource: ExistingPermitTypeDefinition =
            await PermitTypeClient.getType(duplicateFromId);
          return defer({ duplicateSource });
        },
        element: (
          <AwaitRouterData path="duplicateSource">
            {(duplicateSource) => (
              <AddPermitType duplicateFrom={duplicateSource} />
            )}
          </AwaitRouterData>
        ),
      },
      {
        path: Navigation.permitTypesManager.editType.PATH,
        loader: async ({ params }) => {
          const permitTypeId = params.permitTypeId;
          if (!permitTypeId) {
            throw new Response('Permit Type ID is required', { status: 400 });
          }
          const permitType: ExistingPermitTypeDefinition =
            await PermitTypeClient.getType(permitTypeId);
          let customDocumentFile = null;
          if (permitType.isSelfIssued && permitType.jurisdictionUrl) {
            try {
              const { blob, filename } = await DocumentsClient.downloadWithKey(
                permitType.jurisdictionUrl,
              );
              customDocumentFile = {
                file: new File([blob], filename, {
                  type: blob.type,
                }),
                error: false,
              };
            } catch {
              customDocumentFile = {
                file: null,
                error: true,
              };
            }
          }
          return defer({
            permitTypeData: {
              permitType,
              customDocumentFile,
            },
          });
        },
        element: (
          <AwaitRouterData path="permitTypeData">
            {({ permitType, customDocumentFile }) => (
              <EditPermitType
                permitType={permitType}
                customDocumentFile={customDocumentFile}
              />
            )}
          </AwaitRouterData>
        ),
      },
    ],
    errorElement: <GlobalErrorBoundary />,
  },
  {
    path: Navigation.publicPortal,
    element: (
      <ToastProvider>
        <PublicPortal />
      </ToastProvider>
    ),
    errorElement: <GlobalErrorBoundary />,
  },
  {
    path: '*',
    element: <UnknownRoute />,
  },
]);

export const Router = () => {
  const { isLoading, user } = useAuthentication();

  if (isLoading) return <LoadingPage />;

  if (isClientUser(user)) {
    return <ClientUserApp />;
  }

  return <RouterProvider router={router} />;
};
